import { Box, Stack, Switch, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CheckIcon } from "../../../images";
import { ReuseTooltip } from "../../ReuseTooltip";
import { AccordionArrowDown } from "../../../images/AccordionArrowDown";
import { RolePermission } from "../../../../types/apiTypes";

type Props = {
  title: string;
  permissions: RolePermission;
  isExpanded: boolean;
  onExpandedHandler: (title: string) => void;
  changePermissionsHandler: (title: string, permission: string) => void;
};

export const PermissionsItems = ({
  title,
  permissions,
  isExpanded,
  onExpandedHandler,
  changePermissionsHandler,
}: Props) => {
  const [maxHeight, setMaxHeight] = useState<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(isExpanded ? contentRef.current.scrollHeight : 0);
    }
  }, [isExpanded]);

  const changePermissionHandler = (perm: string) => {
    changePermissionsHandler(title, perm);
  };

  const formatPermissionLabel = (title: string) => {
    return title
      .split("_")
      .map((word) =>
        word === "infuse"
          ? "INFUSE"
          : word.charAt(0).toUpperCase() + word.slice(1),
      )
      .join(" ");
  };

  if (
    Object.values(permissions).every(
      ({ is_modifiable, allowed }) => !is_modifiable && !allowed,
    )
  )
    return null;

  return (
    <Stack
      sx={{
        border: "1px solid #DEDEDE",
        borderRadius: "8px",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Stack
        flexDirection={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        onClick={() => onExpandedHandler(title)}
        sx={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#F7F7F7",
          cursor: "pointer",
        }}
      >
        <Typography
          component="span"
          variant="button"
          sx={{
            color: "#232323",
          }}
        >
          {formatPermissionLabel(title).toUpperCase()}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
          }}
        >
          <AccordionArrowDown />
        </Box>
      </Stack>
      <Stack
        ref={contentRef}
        sx={{
          maxHeight,
          opacity: isExpanded ? 1 : 0,
          transition: "max-height 0.3s ease, opacity 0.3s ease",
          overflow: "hidden",
        }}
      >
        {Object.entries(permissions).map(
          ([value, { allowed, is_modifiable }]) => {
            if (!is_modifiable && !allowed) return null;

            return (
              <Stack
                key={value}
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  padding: "8px 16px",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#232323",
                  }}
                >
                  {formatPermissionLabel(value)}
                </Typography>
                <Stack alignItems={"center"} flexDirection={"row"}>
                  {is_modifiable ? (
                    <Switch
                      checked={allowed}
                      onChange={() => changePermissionHandler(value)}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  ) : (
                    <Stack
                      sx={{
                        width: "58px",
                        height: "38px",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <ReuseTooltip
                        text={"This permission is fixed and cannot be changed"}
                        maxWidth={"184px"}
                      >
                        <CheckIcon color={"#585858"} />
                      </ReuseTooltip>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            );
          },
        )}
      </Stack>
    </Stack>
  );
};
